// VER-218: Invites admin screen (Step 11.5). List + create/resend/revoke.
// Create/resend/revoke are admin-only but NOT step-up-gated (backend), so there's
// no OTP modal here. Role options are limited to what this actor may grant.
import { useEffect, useState, useCallback } from "react";
import {
  App,
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Segmented,
  Popconfirm,
  Typography,
} from "antd";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { invitesAdminApi, usersApi } from "../../console/consoleApi";
import { useSession } from "../../console/useSession";
import {
  errorMessage,
  roleLabel,
  roleCategory,
  invitableRolesFor,
  rolesForAccountType,
  ROLES,
  ROLE_LABELS,
} from "../../console/consoleUtils";
import OnboardExistingUserModal from "./OnboardExistingUserModal";

const { Title } = Typography;

const INVITE_STATUS_COLORS = {
  pending: "gold",
  accepted: "green",
  revoked: "red",
  expired: "default",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// `accountId` (optional): when set, this is the drill-in view of ONE company
// (internal admin, inside Accounts). The list is scoped to it AND — crucially — new
// invites are created INTO it, so an internal admin can never mint a company-less user.
// When omitted, it's the account admin's own-org view (session-scoped).
// `accountType` ("external" | "internal") comes from the company being viewed. The
// invite role list is derived from it — an external company offers external roles, an
// internal one offers internal roles — so there's no Internal/External toggle to pick.
export default function ConsoleInvites({ accountId, accountType = "external" } = {}) {
  const { message } = App.useApp();
  const { user: actor } = useSession();
  const isInternalAdmin = actor?.role === ROLES.INTERNAL_ADMIN;
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // Onboard-existing-user is internal-admin only and only inside a company drill-in.
  const canOnboardExisting = isInternalAdmin && accountId != null;
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [apiOptions, setApiOptions] = useState([]); // {value,label} of available API collections
  const [apiMode, setApiMode] = useState("all"); // "all" (unrestricted) | "specific"
  const [form] = Form.useForm();

  // Reference list for the "API access" picker — the APIs THIS admin may grant, bounded by
  // the account's entitlement (VER-222). "All APIs" then means all of the account's APIs.
  useEffect(() => {
    usersApi
      .grantableApis()
      .then((res) =>
        setApiOptions((res.data.data.apiCollections || []).map((n) => ({ value: n, label: n })))
      )
      .catch(() => {}); // optional field — a failure just leaves the list empty
  }, []);

  // Role options = what this actor may grant ∩ what this account's type allows.
  const roleOptions = invitableRolesFor(actor?.role)
    .filter((r) => rolesForAccountType(accountType).includes(r))
    .map((r) => ({ value: r, label: ROLE_LABELS[r] || r }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invitesAdminApi.list(accountId ? { accountId } : undefined);
      const all = res.data.data.invites;
      // The API scopes to the given (or caller's) account. Additionally, a client admin
      // never sees internal-role invites, even if one exists in their account.
      setInvites(isInternalAdmin ? all : all.filter((i) => roleCategory(i.role) === "external"));
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message, isInternalAdmin, accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitInvite = async (values) => {
    setCreating(true);
    try {
      // "All APIs" = empty list (unrestricted; the backend treats empty as full access,
      // future APIs included). "Specific" sends the chosen names.
      const apiCollections = apiMode === "specific" ? values.apiCollections || [] : [];
      await invitesAdminApi.create({
        email: values.email.trim(),
        name: values.name?.trim() || undefined,
        role: values.role,
        // Omit when empty so the backend applies its "empty = all access" default.
        ...(apiCollections.length ? { apiCollections } : {}),
        // Drill-in: target the company being viewed, so the invite lands there and not
        // in the internal admin's own account.
        ...(accountId ? { accountId } : {}),
      });
      message.success(`Invite sent to ${values.email.trim()}.`);
      setModalOpen(false);
      form.resetFields();
      setApiMode("all");
      load();
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const resend = async (id) => {
    try {
      await invitesAdminApi.resend(id);
      message.success("Invite resent — a fresh link is on its way.");
      load();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const revoke = async (id) => {
    try {
      await invitesAdminApi.revoke(id);
      message.success("Invite revoked.");
      load();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const columns = [
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Name", dataIndex: "name", key: "name", render: (n) => n || "—" },
    { title: "Role", dataIndex: "role", key: "role", render: (r) => <Tag>{roleLabel(r)}</Tag> },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={INVITE_STATUS_COLORS[s]}>{s}</Tag>,
    },
    { title: "Expires", dataIndex: "expiresAt", key: "expiresAt", render: fmtDate },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, row) =>
        row.status === "pending" ? (
          <Space>
            <Button size="small" onClick={() => resend(row.id)}>
              Resend
            </Button>
            <Popconfirm
              title="Revoke this invite?"
              okText="Revoke"
              okButtonProps={{ danger: true }}
              onConfirm={() => revoke(row.id)}
            >
              <Button size="small" danger>
                Revoke
              </Button>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          Invites
        </Title>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
          {canOnboardExisting && (
            <Button onClick={() => setOnboardOpen(true)}>Onboard existing user</Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setApiMode("all");
              setModalOpen(true);
            }}
          >
            Invite user
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={invites}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />

      <Modal
        title="Invite a user"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Send invite"
        confirmLoading={creating}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={submitInvite} requiredMark={false}>
          {/* No Internal/External toggle — the account's type already decides which
              roles are on offer (see roleOptions). */}
          <Form.Item name="name" label="Name (optional)">
            <Input placeholder="Jane Doe" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
          >
            <Input placeholder="person@company.com" autoComplete="off" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: "Pick a role" }]}>
            <Select placeholder="Select a role" options={roleOptions} />
          </Form.Item>
          <Form.Item label="API access" style={{ marginBottom: 8 }}>
            <Segmented
              value={apiMode}
              onChange={setApiMode}
              options={[
                { label: "All APIs (full access)", value: "all" },
                { label: "Specific APIs", value: "specific" },
              ]}
            />
          </Form.Item>
          {apiMode === "specific" && (
            <Form.Item
              name="apiCollections"
              label="Which APIs"
              rules={[{ required: true, message: "Pick at least one API, or choose All APIs" }]}
              help="Exactly these APIs. (All APIs also covers ones added later.) Changeable under Users."
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="Select APIs to grant"
                options={apiOptions}
                maxTagCount="responsive"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {canOnboardExisting && (
        <OnboardExistingUserModal
          open={onboardOpen}
          onClose={() => setOnboardOpen(false)}
          onDone={() => {
            setOnboardOpen(false);
            load();
          }}
          accountId={accountId}
          accountType={accountType}
        />
      )}
    </Card>
  );
}
